import { useEffect, useState } from "react";
import { resolveMediaUrl } from "../../../../../utils/mediaUrl";
import {
    ensurePosterFontsLoaded,
    paperBox,
    posterFontStack,
    posterQrTheme,
} from "./constants";
import { BrandMark, PlaceholderQr, PosterIcon } from "./icons";
import { recolorQrCode } from "./recolorQrCode";
import "./PrintPosterPreview.scss";

export default function PrintPosterPreview({
    poster,
    company,
    itemName,
    qrSrc,
    previewRef,
}) {
    const colors = poster?.colors || {};
    const box = paperBox(poster);
    const logoSrc =
        poster?.show_logo && company?.logo
            ? resolveMediaUrl(company.logo)
            : null;
    const values = Array.isArray(poster?.values) ? poster.values : [];
    const showValues = poster?.show_values !== false && values.length > 0;
    const qrTheme = posterQrTheme(poster);
    const [renderedQrSrc, setRenderedQrSrc] = useState("");
    const badgeText =
        poster?.show_badge && (poster.badge_label || itemName)
            ? [poster.badge_label, itemName].filter(Boolean).join(": ")
            : "";
    const focus = poster?.focus_element || "qr";
    const layout = poster?.layout || "stack";
    const ornament = poster?.ornament || "frame";
    const align = poster?.align || "center";
    const qrSize = poster?.qr_size || "medium";
    const showBrandMark = poster?.show_logo !== false;

    useEffect(() => {
        ensurePosterFontsLoaded();
    }, []);

    useEffect(() => {
        let cancelled = false;
        if (!qrSrc) {
            setRenderedQrSrc("");
            return undefined;
        }
        setRenderedQrSrc("");
        recolorQrCode(qrSrc, qrTheme)
            .then((result) => {
                if (!cancelled) setRenderedQrSrc(result);
            })
            .catch(() => {
                if (!cancelled) setRenderedQrSrc(qrSrc);
            });
        return () => {
            cancelled = true;
        };
    }, [qrSrc, qrTheme.foreground, qrTheme.background]);

    return (
        <article
            ref={previewRef}
            className={`print-poster print-poster--${poster?.template_id || "classic-gold"}`}
            data-paper={poster?.paper_size || "a5"}
            data-focus={focus}
            data-layout={layout}
            data-ornament={ornament}
            data-align={align}
            data-qr-size={qrSize}
            data-has-values={showValues ? "true" : "false"}
            data-page-size={box.pageSize}
            dir="rtl"
            style={{
                "--poster-bg": colors.background,
                "--poster-text": colors.text,
                "--poster-accent": colors.accent,
                "--poster-muted": colors.muted,
                "--poster-font": posterFontStack(poster?.font_family),
                "--poster-qr-fg": qrTheme.foreground,
                "--poster-qr-bg": qrTheme.background,
                width: `${box.widthMm}mm`,
                height: `${box.heightMm}mm`,
            }}
        >
            <div className="print-poster__sheet">
                <div className="print-poster__glow" aria-hidden="true" />
                <div className="print-poster__waves" aria-hidden="true">
                    <svg viewBox="0 0 640 150" preserveAspectRatio="none">
                        {[18, 42, 66, 90, 114, 138].map((offset) => (
                            <path
                                key={offset}
                                d={`M-20 ${offset + 28} C 100 ${offset - 20}, 190 ${offset - 20}, 320 ${offset + 28} S 540 ${offset + 76}, 660 ${offset + 28}`}
                            />
                        ))}
                    </svg>
                </div>
                <header className="print-poster__brand">
                    {showBrandMark ? (
                        <div className="print-poster__brand-media">
                            {logoSrc ? (
                                <img
                                    className="print-poster__logo"
                                    src={logoSrc}
                                    alt=""
                                />
                            ) : (
                                <BrandMark color={colors.accent} />
                            )}
                        </div>
                    ) : null}
                    <div className="print-poster__brand-copy">
                        {poster?.brand_name ? (
                            <p className="print-poster__brand-name">
                                {poster.brand_name}
                            </p>
                        ) : null}
                        {poster?.brand_subtitle ? (
                            <p className="print-poster__brand-sub">
                                {poster.brand_subtitle}
                            </p>
                        ) : null}
                        {poster?.affiliation ? (
                            <p className="print-poster__affiliation">
                                {poster.affiliation}
                            </p>
                        ) : null}
                    </div>
                </header>
                <div className="print-poster__copy">
                    {poster?.headline ? (
                        <h1 className="print-poster__headline">
                            {poster.headline}
                        </h1>
                    ) : null}
                    {poster?.subtitle ? (
                        <h2 className="print-poster__subtitle">
                            {poster.subtitle}
                        </h2>
                    ) : null}
                    {poster?.instruction ? (
                        <p className="print-poster__instruction">
                            {poster.instruction}
                        </p>
                    ) : null}
                </div>
                <div className="print-poster__qr-wrap">
                    <div className="print-poster__qr-group">
                        <div className="print-poster__qr-stage">
                            <span
                                className="print-poster__qr-flourish"
                                aria-hidden="true"
                            />
                            <div className="print-poster__qr-frame">
                                {renderedQrSrc || qrSrc ? (
                                    <img src={renderedQrSrc || qrSrc} alt="" />
                                ) : (
                                    <PlaceholderQr />
                                )}
                            </div>
                            <span
                                className="print-poster__qr-flourish"
                                aria-hidden="true"
                            />
                        </div>
                    </div>
                </div>
                {showValues ? (
                    <ul className="print-poster__values">
                        {values.map((value, index) => (
                            <li key={`${value.icon}-${index}`}>
                                <span className="print-poster__value-icon">
                                    <PosterIcon
                                        name={value.icon}
                                        color={colors.accent}
                                    />
                                </span>
                                <span>{value.text}</span>
                            </li>
                        ))}
                    </ul>
                ) : null}
                <footer className="print-poster__footer">
                    {poster?.footer ? <p>{poster.footer}</p> : null}
                    {poster?.signoff ? (
                        <p className="print-poster__signoff">{poster.signoff}</p>
                    ) : null}
                    {badgeText ? (
                        <div className="print-poster__badge">{badgeText}</div>
                    ) : null}
                </footer>
            </div>
        </article>
    );
}
